import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import "@/App.css";

// Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Icons
import { 
  ShoppingCart, Search, Menu, User, Package, Sun, Moon, ChevronRight, 
  Minus, Plus, Trash2, X, Filter, LogOut, Settings, History, 
  Smartphone, Battery, Wrench, Monitor, Home, ArrowRight, Star,
  Truck, Shield, CreditCard, Clock, LayoutDashboard, Box, Users,
  TrendingUp, AlertCircle, CheckCircle, ExternalLink, RefreshCw, Phone
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== ERROR BOUNDARY ==============
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('React Error Boundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <h1 className="text-2xl font-bold">Ocurrio un error</h1>
            <p className="text-muted-foreground">Intenta recargar la pagina</p>
            <button
              onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
              className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              Recargar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============== CONTEXT ==============

const AppContext = createContext();

export const useApp = () => useContext(AppContext);

const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark';
    }
    return false;
  });
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('guest_session_id'));

  const api = useMemo(() => axios.create({
    baseURL: API,
  }), []);

  // Theme toggle
  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  // Check auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (err) {
        console.error('Auth check failed:', err?.response?.status);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [api]);

  // Fetch cart
  const fetchCart = useCallback(async () => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      const response = await api.get('/cart', { params });
      setCart({ items: response.data.items || [], total: response.data.total || 0 });
    } catch (err) {
      console.error('Cart fetch failed:', err?.message);
    }
  }, [sessionId, api]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart, user]);

  const addToCart = async (productId, quantity = 1) => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      const response = await api.post('/cart/add', { product_id: productId, quantity }, { params });
      if (response.data.session_id && !user) {
        setSessionId(response.data.session_id);
        localStorage.setItem('guest_session_id', response.data.session_id);
      }
      await fetchCart();
      toast.success('Agregado al carrito');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar');
    }
  };

  const updateCartItem = async (productId, quantity) => {
    try {
      const params = { product_id: productId, quantity, ...(sessionId ? { session_id: sessionId } : {}) };
      await api.put('/cart/update', null, { params });
      await fetchCart();
    } catch (err) {
      console.error('Cart update failed:', err?.message);
      toast.error('Error al actualizar carrito');
    }
  };

  const clearCart = async () => {
    try {
      const params = sessionId ? { session_id: sessionId } : {};
      await api.delete('/cart/clear', { params });
      setCart({ items: [], total: 0 });
    } catch (err) {
      console.error('Cart clear failed:', err?.message);
      toast.error('Error al vaciar carrito');
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(null);
      setCart({ items: [], total: 0 });
      toast.success('Sesión cerrada');
    } catch (err) {
      console.error('Logout failed:', err?.message);
      setUser(null);
    }
  };

  const value = {
    user, setUser, cart, addToCart, updateCartItem, clearCart, fetchCart,
    isDark, setIsDark, loading, api, logout, sessionId
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

// ============== LAYOUT COMPONENTS ==============

const Header = () => {
  const { user, cart, isDark, setIsDark, logout } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/productos?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const cartItemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" data-testid="header">
      {/* Top bar with phone */}
      <div className="w-full bg-primary/5 border-b border-primary/10">
        <div className="container mx-auto px-4 flex items-center justify-center py-1.5 gap-2">
          <Phone className="h-4 w-4 text-primary" />
          <a href="https://wa.me/5491128597105" className="text-sm font-semibold tracking-wide text-primary hover:underline" data-testid="top-phone-link">
            +54 (11) 2859-7105
          </a>
        </div>
      </div>
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 font-bold text-xl" data-testid="logo">
            <img src="/logo_triton_tech.jpg" alt="TRITON TECH" className="h-9 w-9 rounded-md object-cover" />
            <span className="hidden sm:inline">TRITON TECH</span>
          </Link>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1 max-w-xl hidden md:flex">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar repuestos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
                data-testid="search-input"
              />
            </div>
          </form>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsDark(!isDark)}
              data-testid="theme-toggle"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            {/* Cart Sheet */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="cart-button">
                  <ShoppingCart className="h-5 w-5" />
                  {cartItemCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-accent text-white text-xs">
                      {cartItemCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent data-testid="cart-sheet">
                <SheetHeader>
                  <SheetTitle>Carrito ({cartItemCount})</SheetTitle>
                </SheetHeader>
                <CartDrawer />
              </SheetContent>
            </Sheet>

            {/* User Menu */}
            {user ? (
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu-button">
                    <User className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent>
                  <SheetHeader>
                    <SheetTitle>Mi Cuenta</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="h-12 w-12 rounded-full" />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-6 w-6 text-primary" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <Separator />
                    <nav className="space-y-2">
                      <Link to="/mis-pedidos" className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                        <History className="h-5 w-5" />
                        <span>Mis Pedidos</span>
                      </Link>
                      <Link to="/perfil" className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                        <Settings className="h-5 w-5" />
                        <span>Configuración</span>
                      </Link>
                      {user.role === 'admin' && (
                        <Link to="/admin" className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors text-primary">
                          <LayoutDashboard className="h-5 w-5" />
                          <span>Panel Admin</span>
                        </Link>
                      )}
                    </nav>
                    <Separator />
                    <Button variant="outline" className="w-full" onClick={logout} data-testid="logout-button">
                      <LogOut className="h-4 w-4 mr-2" />
                      Cerrar Sesión
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            ) : (
              <Button asChild variant="default" size="sm" data-testid="login-button">
                <Link to="/login">Ingresar</Link>
              </Button>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" data-testid="mobile-menu-sheet">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 space-y-3">
                  <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                    <Home className="h-5 w-5" />
                    <span>Inicio</span>
                  </Link>
                  <Link to="/productos" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                    <Package className="h-5 w-5" />
                    <span>Productos</span>
                  </Link>
                  <Link to="/productos?category=cat_modulos" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                    <Smartphone className="h-5 w-5" />
                    <span>Modulos</span>
                  </Link>
                  <Link to="/productos?brand=Samsung" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                    <Monitor className="h-5 w-5" />
                    <span>Samsung</span>
                  </Link>
                  <Link to="/productos?brand=Motorola" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                    <Smartphone className="h-5 w-5" />
                    <span>Motorola</span>
                  </Link>
                  <Link to="/productos?brand=iPhone" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors">
                    <Smartphone className="h-5 w-5" />
                    <span>iPhone</span>
                  </Link>
                  <Separator />
                  <a href="https://wa.me/5491128597105" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-md hover:bg-muted transition-colors text-green-600">
                    <Phone className="h-5 w-5" />
                    <span>WhatsApp</span>
                  </a>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Mobile Search */}
        <form onSubmit={handleSearch} className="pb-4 md:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar repuestos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </form>
      </div>

      {/* Categories Nav */}
      <nav className="border-t bg-muted/30 hidden md:block">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6 h-10 text-sm overflow-x-auto no-scrollbar">
            <Link to="/productos?category=cat_modulos" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">Módulos</Link>
            <Link to="/productos?category=cat_computacion" className="text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap">Computación</Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

const Footer = () => (
  <footer className="border-t bg-muted/30 mt-auto" data-testid="footer">
    <div className="container mx-auto px-4 py-12">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <div className="flex items-center gap-2 font-bold text-xl mb-4">
            <img src="/logo_triton_tech.jpg" alt="TRITON TECH" className="h-10 w-10 rounded-md object-cover" />
            <span>TRITON TECH</span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Descubrí lo último en tecnología móvil. Módulos y celulares de Samsung y Motorola, accesorios inteligentes y lo mejor en tecnología.
          </p>
          <div className="flex items-center gap-3">
            <a href="https://www.instagram.com/tritontech2026/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-muted hover:bg-primary hover:text-white transition-colors">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
            </a>
            <a href="https://wa.me/5491128597105" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-muted hover:bg-green-500 hover:text-white transition-colors">
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </a>
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Categorías</h4>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li><Link to="/productos?category=cat_modulos" className="hover:text-foreground transition-colors">Módulos</Link></li>
            <li><Link to="/productos?category=cat_computacion" className="hover:text-foreground transition-colors">Computación</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Contacto</h4>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-center gap-2">
              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <a href="https://wa.me/5491128597105" className="hover:text-foreground transition-colors font-medium">+54 (11) 2859-7105</a>
            </li>
            <li className="flex items-center gap-2">
              <svg className="h-4 w-4 text-pink-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              <a href="https://www.instagram.com/tritontech2026/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">@tritontech2026</a>
            </li>
          </ul>
          <div className="mt-4">
            <h5 className="font-medium text-sm mb-2">Horarios de Atención</h5>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li>Lun - Vie: 8:00 - 20:00</li>
              <li>Sábado: 9:00 - 18:00</li>
              <li>Domingo: Cerrado</li>
            </ul>
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Pagos Seguros</h4>
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <CreditCard className="h-8 w-8" />
            <span className="text-sm">Mercado Pago</span>
          </div>
          <p className="text-xs text-muted-foreground mb-4">Hasta 12 cuotas sin interés con tarjetas de crédito</p>
          <div className="space-y-1 text-xs text-muted-foreground">
            <p>Visa • MasterCard • Amex</p>
            <p>Transferencia Bancaria</p>
          </div>
        </div>
      </div>
      <Separator className="my-8" />
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
        <p>© 2024 TRITON TECH. Todos los derechos reservados.</p>
        <div className="flex items-center gap-4">
          <a href="https://www.tritontech.com.ar" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">www.tritontech.com.ar</a>
          <Link to="#" className="hover:text-foreground transition-colors">Términos</Link>
          <Link to="#" className="hover:text-foreground transition-colors">Privacidad</Link>
        </div>
      </div>
    </div>
  </footer>
);

const CartDrawer = () => {
  const { cart, updateCartItem, clearCart } = useApp();
  const navigate = useNavigate();

  if (cart.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center">
        <ShoppingCart className="h-16 w-16 text-muted-foreground mb-4" />
        <p className="text-lg font-medium">Tu carrito está vacío</p>
        <p className="text-sm text-muted-foreground mt-2">Agrega productos para comenzar</p>
        <Button asChild className="mt-6 btn-accent">
          <Link to="/productos">Ver Productos</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {cart.items.map((item) => (
          <div key={item.product_id} className="flex gap-3 p-3 rounded-lg border bg-card" data-testid={`cart-item-${item.product_id}`}>
            {item.image && (
              <img src={item.image} alt={item.title} className="w-16 h-16 object-cover rounded" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{item.title}</p>
              <p className="text-price text-sm mt-1">${item.price.toLocaleString('es-AR')}</p>
              <div className="flex items-center gap-2 mt-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-7 w-7"
                  onClick={() => updateCartItem(item.product_id, item.quantity - 1)}
                >
                  <Minus className="h-3 w-3" />
                </Button>
                <span className="w-8 text-center text-sm">{item.quantity}</span>
                <Button 
                  variant="outline" 
                  size="icon" 
                  className="h-7 w-7"
                  onClick={() => updateCartItem(item.product_id, item.quantity + 1)}
                >
                  <Plus className="h-3 w-3" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 text-destructive ml-auto"
                  onClick={() => updateCartItem(item.product_id, 0)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t pt-4 space-y-4">
        <div className="flex justify-between text-lg font-semibold">
          <span>Total</span>
          <span className="text-price">${cart.total.toLocaleString('es-AR')}</span>
        </div>
        <Button className="w-full btn-accent" onClick={() => navigate('/checkout')} data-testid="checkout-button">
          Ir al Checkout
        </Button>
        <Button variant="outline" className="w-full" onClick={clearCart}>
          Vaciar Carrito
        </Button>
      </div>
    </div>
  );
};

// ============== PAGES ==============

const HomePage = () => {
  const { api } = useApp();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [prodRes, catRes] = await Promise.all([
          api.get('/products/featured?limit=16'),
          api.get('/categories')
        ]);
        setProducts(prodRes.data.products);
        setCategories(catRes.data.categories);
      } catch (err) {
        console.error('HomePage fetch failed:', err?.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [api]);

  const motorolaProducts = products.filter(p => p.brand === 'Motorola');
  const samsungProducts = products.filter(p => p.brand === 'Samsung');
  const iphoneProducts = products.filter(p => p.brand === 'iPhone');

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-4 py-12 md:py-20 flex flex-col md:flex-row items-center gap-8">
          <div className="flex-1">
            <Badge className="mb-4 bg-orange-500 text-white border-0 text-sm px-3 py-1">Ofertas Especiales</Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight mb-4">
              Modulos y Repuestos para Celulares
            </h1>
            <p className="text-base text-gray-300 mb-6 leading-relaxed">
              Pantallas OLED, displays y modulos para Samsung, Motorola e iPhone. Calidad premium con garantia. Envio a todo el pais.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-orange-500 hover:bg-orange-600 text-white text-base font-bold px-6">
                <Link to="/productos">
                  Ver Catalogo
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <Link to="/productos?brand=Samsung">Samsung</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <Link to="/productos?brand=Motorola">Motorola</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                <Link to="/productos?brand=iPhone">iPhone</Link>
              </Button>
            </div>
            <div className="flex items-center gap-4 mt-6 text-sm text-gray-400">
              <span className="flex items-center gap-1"><Truck className="h-4 w-4 text-green-400" /> Envio gratis</span>
              <span className="flex items-center gap-1"><CreditCard className="h-4 w-4 text-blue-400" /> 6 cuotas sin interes</span>
              <span className="flex items-center gap-1"><Shield className="h-4 w-4 text-yellow-400" /> 90 dias garantia</span>
            </div>
          </div>
          <div className="flex-1 hidden md:flex justify-center">
            <img src="/logo_triton_tech.jpg" alt="TRITON TECH" className="w-64 h-64 rounded-2xl object-cover shadow-2xl" />
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-4 bg-orange-500 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { icon: <Truck className="h-5 w-5" />, title: 'Envio Gratis', desc: 'A todo el pais' },
              { icon: <CreditCard className="h-5 w-5" />, title: '6 Cuotas', desc: 'Sin interes' },
              { icon: <Shield className="h-5 w-5" />, title: 'Garantia', desc: '90 dias' },
              { icon: <Phone className="h-5 w-5" />, title: 'Soporte', desc: 'WhatsApp directo' },
            ].map((f) => (
              <div key={f.title} className="flex items-center justify-center gap-2">
                {f.icon}
                <div className="text-left">
                  <p className="font-bold text-sm">{f.title}</p>
                  <p className="text-xs text-white/80">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ofertas Section - Horizontal Scroll */}
      <section className="py-10">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold">Ofertas por tiempo LIMITADO!</h2>
              <p className="text-sm text-muted-foreground">Los mejores precios en modulos</p>
            </div>
            <Button asChild variant="outline" size="sm">
              <Link to="/productos">Ver Todos <ChevronRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(5)].map((_, i) => (
                <Card key={`skeleton-offer-${i}`} className="overflow-hidden"><Skeleton className="h-48 w-full" /><CardContent className="p-3 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-5 w-1/2" /></CardContent></Card>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-4" style={{ minWidth: 'max-content' }}>
                {products.slice(0, 10).map((product, i) => (
                  <div key={product.product_id} className="w-[220px] flex-shrink-0">
                    <ProductCard product={product} index={i} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Category Banners */}
      <section className="py-8 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Link to="/productos?brand=Samsung" className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-600 to-blue-800 p-6 text-white hover:shadow-xl transition-shadow" data-testid="banner-samsung">
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-1">Samsung</h3>
                <p className="text-sm text-blue-200">Modulos Galaxy A y S</p>
                <span className="inline-flex items-center mt-3 text-sm font-medium group-hover:underline">Ver mas <ChevronRight className="h-4 w-4 ml-1" /></span>
              </div>
              <Smartphone className="absolute right-4 bottom-4 h-20 w-20 text-white/10" />
            </Link>
            <Link to="/productos?brand=Motorola" className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-600 to-purple-800 p-6 text-white hover:shadow-xl transition-shadow" data-testid="banner-motorola">
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-1">Motorola</h3>
                <p className="text-sm text-indigo-200">Modulos Edge y Moto G</p>
                <span className="inline-flex items-center mt-3 text-sm font-medium group-hover:underline">Ver mas <ChevronRight className="h-4 w-4 ml-1" /></span>
              </div>
              <Smartphone className="absolute right-4 bottom-4 h-20 w-20 text-white/10" />
            </Link>
            <Link to="/productos?brand=iPhone" className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-700 to-gray-900 p-6 text-white hover:shadow-xl transition-shadow col-span-2 md:col-span-1" data-testid="banner-iphone">
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-1">iPhone</h3>
                <p className="text-sm text-gray-300">Modulos OLED iPhone</p>
                <span className="inline-flex items-center mt-3 text-sm font-medium group-hover:underline">Ver mas <ChevronRight className="h-4 w-4 ml-1" /></span>
              </div>
              <Smartphone className="absolute right-4 bottom-4 h-20 w-20 text-white/10" />
            </Link>
          </div>
        </div>
      </section>

      {/* Los mas elegidos - Samsung */}
      {samsungProducts.length > 0 && (
        <section className="py-10">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold">Samsung - Los mas elegidos</h2>
              <Button asChild variant="outline" size="sm">
                <Link to="/productos?brand=Samsung">Ver Todos <ChevronRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {samsungProducts.slice(0, 4).map((product, i) => (
                <ProductCard key={product.product_id} product={product} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Los mas elegidos - Motorola */}
      {motorolaProducts.length > 0 && (
        <section className="py-10 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold">Motorola - Novedades</h2>
              <Button asChild variant="outline" size="sm">
                <Link to="/productos?brand=Motorola">Ver Todos <ChevronRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {motorolaProducts.slice(0, 4).map((product, i) => (
                <ProductCard key={product.product_id} product={product} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* iPhone */}
      {iphoneProducts.length > 0 && (
        <section className="py-10">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-bold">iPhone - Modulos OLED</h2>
              <Button asChild variant="outline" size="sm">
                <Link to="/productos?brand=iPhone">Ver Todos <ChevronRight className="ml-1 h-4 w-4" /></Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {iphoneProducts.slice(0, 4).map((product, i) => (
                <ProductCard key={product.product_id} product={product} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section className="py-12">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-xl md:text-2xl font-bold mb-4">Quienes somos...</h2>
          <p className="text-muted-foreground leading-relaxed">
            En Triton Tech contamos con experiencia brindando soluciones en tecnologia movil y repuestos de calidad premium. 
            Nos especializamos en modulos OLED para Samsung, Motorola e iPhone, ofreciendo productos de primera calidad, 
            atencion personalizada y un compromiso constante con la satisfaccion de nuestros clientes.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Sos tecnico o tenes un local?</h2>
          <p className="text-gray-400 mb-6 max-w-xl mx-auto">
            Consulta por precios mayoristas y beneficios exclusivos via WhatsApp.
          </p>
          <Button asChild size="lg" className="bg-green-500 hover:bg-green-600 text-white font-bold px-8">
            <a href="https://wa.me/5491128597105" target="_blank" rel="noopener noreferrer">
              Contactar por WhatsApp
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

const ProductCard = ({ product, index = 0 }) => {
  const { addToCart } = useApp();
  const discount = product.compare_price 
    ? Math.round((1 - product.price / product.compare_price) * 100) 
    : null;
  const cuota6 = Math.round(product.price / 6);
  const precioTransf = Math.round(product.price * 0.85);

  return (
    <Card 
      className="product-card overflow-hidden group stagger-item border hover:shadow-lg transition-shadow"
      style={{ animationDelay: `${index * 0.05}s` }}
      data-testid={`product-card-${product.product_id}`}
    >
      <Link to={`/producto/${product.product_id}`}>
        <div className="relative aspect-square overflow-hidden bg-white">
          {product.images?.[0] ? (
            <img 
              src={product.images[0]} 
              alt={product.title}
              className="product-image w-full h-full object-contain p-2"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="h-12 w-12 text-muted-foreground" />
            </div>
          )}
          {discount && (
            <Badge className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold">
              {discount}% OFF
            </Badge>
          )}
          <Badge className="absolute top-2 right-2 bg-green-500 text-white text-xs">
            Envio gratis
          </Badge>
          {product.quantity <= 0 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Badge variant="destructive">Sin Stock</Badge>
            </div>
          )}
        </div>
      </Link>
      <CardContent className="p-3">
        <Link to={`/producto/${product.product_id}`}>
          <h3 className="font-medium text-sm line-clamp-2 hover:text-primary transition-colors min-h-[2.5rem]">
            {product.title}
          </h3>
        </Link>
        <div className="mt-2">
          <span className="text-xl font-bold text-foreground">${product.price.toLocaleString('es-AR')}</span>
          {product.compare_price && (
            <span className="text-sm text-muted-foreground line-through ml-2">
              ${product.compare_price.toLocaleString('es-AR')}
            </span>
          )}
        </div>
        <p className="text-xs text-green-600 font-medium mt-1">
          6 x ${cuota6.toLocaleString('es-AR')} sin interes
        </p>
        <p className="text-xs text-blue-600 font-medium mt-0.5">
          ${precioTransf.toLocaleString('es-AR')} con Transferencia
        </p>
        {product.quantity > 0 && product.quantity <= 5 && (
          <p className="text-xs text-orange-500 font-semibold mt-1">
            Ultimo disponible!
          </p>
        )}
      </CardContent>
      <CardFooter className="p-3 pt-0">
        <Button 
          className="w-full btn-accent"
          disabled={product.quantity <= 0}
          onClick={(e) => {
            e.preventDefault();
            addToCart(product.product_id);
          }}
          data-testid={`add-to-cart-${product.product_id}`}
        >
          <ShoppingCart className="h-4 w-4 mr-2" />
          Comprar
        </Button>
      </CardFooter>
    </Card>
  );
};

const ProductsPage = () => {
  const { api } = useApp();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const location = useLocation();

  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      category: params.get('category') || '',
      brand: params.get('brand') || '',
      search: params.get('search') || '',
      sort: 'created_at',
      order: 'desc',
      in_stock: false
    };
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newCategory = params.get('category') || '';
    const newSearch = params.get('search') || '';
    const newBrand = params.get('brand') || '';
    setFilters(prev => {
      if (prev.category === newCategory && prev.search === newSearch && prev.brand === newBrand) return prev;
      return { ...prev, category: newCategory, search: newSearch, brand: newBrand };
    });
  }, [location.search]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {
          limit: 100,
          sort: filters.sort,
          order: filters.order,
          ...(filters.category && { category: filters.category }),
          ...(filters.brand && { brand: filters.brand }),
          ...(filters.search && { search: filters.search }),
          ...(filters.in_stock && { in_stock: true })
        };
        const response = await api.get('/products', { params });
        setProducts(response.data.products);
        setTotal(response.data.total);
      } catch (err) {
        console.error('Products fetch failed:', err?.message);
        toast.error('Error al cargar productos');
      } finally {
        setLoading(false);
      }
    };

    const fetchFilters = async () => {
      try {
        const [catRes, brandRes] = await Promise.all([
          api.get('/categories'),
          api.get('/brands')
        ]);
        setCategories(catRes.data.categories);
        setBrands(brandRes.data.brands);
      } catch (err) {
        console.error('Filters fetch failed:', err?.message);
      }
    };

    fetchProducts();
    fetchFilters();
  }, [api, filters]);

  return (
    <div className="container mx-auto px-4 py-8" data-testid="products-page">
      <div className="flex flex-col md:flex-row gap-8">
        {/* Filters Sidebar */}
        <aside className="md:w-64 shrink-0 space-y-6">
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground">Categoría</Label>
                <Select value={filters.category || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, category: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="category-filter">
                    <SelectValue placeholder="Todas las categorías" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.category_id} value={cat.category_id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground">Marca</Label>
                <Select value={filters.brand || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, brand: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="brand-filter">
                    <SelectValue placeholder="Todas las marcas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground">Ordenar por</Label>
                <Select 
                  value={`${filters.sort}-${filters.order}`} 
                  onValueChange={(v) => {
                    const [sort, order] = v.split('-');
                    setFilters(prev => ({ ...prev, sort, order }));
                  }}
                >
                  <SelectTrigger data-testid="sort-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at-desc">Más recientes</SelectItem>
                    <SelectItem value="price-asc">Menor precio</SelectItem>
                    <SelectItem value="price-desc">Mayor precio</SelectItem>
                    <SelectItem value="title-asc">A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={filters.in_stock}
                  onChange={(e) => setFilters(prev => ({ ...prev, in_stock: e.target.checked }))}
                  className="rounded border-input"
                />
                <span className="text-sm">Solo con stock</span>
              </label>
            </div>
          </div>
        </aside>

        {/* Products Grid */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-6">
            <p className="text-muted-foreground">
              {total} productos encontrados
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <Card key={`skeleton-prod-${i}`} className="overflow-hidden">
                  <Skeleton className="h-48 w-full" />
                  <CardContent className="p-4 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-6 w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-20">
              <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No se encontraron productos</h3>
              <p className="text-muted-foreground">Intenta ajustar los filtros</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map((product, i) => (
                <ProductCard key={product.product_id} product={product} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductDetailPage = () => {
  const { api, addToCart } = useApp();
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/products/${productId}`);
        setProduct(response.data);
      } catch (err) {
        console.error('Product fetch failed:', err?.message);
        toast.error('Error al cargar producto');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [api, productId]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <Skeleton className="aspect-square" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-12 w-1/3" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-xl font-semibold">Producto no encontrado</h2>
        <Button asChild className="mt-4">
          <Link to="/productos">Ver Productos</Link>
        </Button>
      </div>
    );
  }

  const discount = product.compare_price 
    ? Math.round((1 - product.price / product.compare_price) * 100) 
    : null;

  return (
    <div className="container mx-auto px-4 py-8" data-testid="product-detail-page">
      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* Images */}
        <div className="space-y-4">
          <div className="aspect-square rounded-lg overflow-hidden bg-muted border">
            {product.images?.[selectedImage] ? (
              <img 
                src={product.images[selectedImage]} 
                alt={product.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="h-24 w-24 text-muted-foreground" />
              </div>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {product.images.map((img, i) => (
                <button
                  key={`thumb-${i}`}
                  onClick={() => setSelectedImage(i)}
                  className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                    selectedImage === i ? 'border-primary' : 'border-transparent'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-6">
          <div>
            <p className="sku-text mb-2">{product.sku}</p>
            <h1 className="text-2xl md:text-3xl font-bold">{product.title}</h1>
            {product.brand && (
              <Badge variant="secondary" className="mt-2">{product.brand}</Badge>
            )}
          </div>

          <div className="flex items-baseline gap-3">
            <span className="text-price text-3xl">${product.price.toLocaleString('es-AR')}</span>
            {product.compare_price && (
              <>
                <span className="text-lg text-muted-foreground line-through">
                  ${product.compare_price.toLocaleString('es-AR')}
                </span>
                <Badge className="bg-accent text-white">-{discount}%</Badge>
              </>
            )}
          </div>

          <p className="text-sm text-success flex items-center gap-1">
            <CreditCard className="h-4 w-4" />
            Hasta 12 cuotas sin interés de ${Math.round(product.price / 12).toLocaleString('es-AR')}
          </p>

          <Separator />

          {product.quantity > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <Label>Cantidad</Label>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-12 text-center">{quantity}</span>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setQuantity(Math.min(product.quantity, quantity + 1))}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <span className="text-sm text-muted-foreground">
                  {product.quantity} disponibles
                </span>
              </div>

              <Button 
                size="lg" 
                className="w-full btn-accent"
                onClick={() => addToCart(product.product_id, quantity)}
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Agregar al Carrito
              </Button>
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-destructive font-medium flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Producto sin stock
              </p>
            </div>
          )}

          <Separator />

          {/* Description */}
          {product.description && (
            <div>
              <h3 className="font-semibold mb-2">Descripción</h3>
              <p className="text-muted-foreground">{product.description}</p>
            </div>
          )}

          {/* Specifications */}
          {product.specs && Object.keys(product.specs).length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Caracteristicas Tecnicas</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                {Object.entries(product.specs).map(([key, value]) => (
                  <div key={key} className="flex justify-between p-3 bg-muted rounded-lg">
                    <span className="text-muted-foreground">{key}</span>
                    <span className="font-medium text-right">{String(value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Shipping info */}
          <div className="p-4 rounded-lg border bg-card space-y-3">
            <div className="flex items-center gap-3">
              <Truck className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium">Envío a todo el país</p>
                <p className="text-sm text-muted-foreground">Gratis en compras mayores a $50.000</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium">Garantía de 90 días</p>
                <p className="text-sm text-muted-foreground">Cambio o devolución sin costo</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoginPage = () => {
  const { user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4" data-testid="login-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Smartphone className="h-12 w-12 text-primary" />
          </div>
          <CardTitle className="text-2xl">Bienvenido a TRITON TECH</CardTitle>
          <p className="text-muted-foreground">Ingresá para continuar</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            variant="outline" 
            className="w-full h-12"
            onClick={handleGoogleLogin}
            data-testid="google-login-button"
          >
            <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar con Google
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">O</span>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground">
            Al continuar, aceptás nuestros{' '}
            <Link to="#" className="text-primary hover:underline">Términos</Link>
            {' '}y{' '}
            <Link to="#" className="text-primary hover:underline">Política de Privacidad</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const AuthCallbackPage = () => {
  const { api, setUser } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        toast.error('Error de autenticación');
        navigate('/login');
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await api.post(`/auth/session?session_id=${sessionId}`);
        setUser(response.data);
        toast.success('¡Bienvenido!');
        navigate('/', { state: { user: response.data } });
      } catch (error) {
        toast.error('Error al iniciar sesión');
        navigate('/login');
      }
    };

    processAuth();
  }, [api, setUser, navigate, location.hash]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p>Procesando autenticación...</p>
      </div>
    </div>
  );
};

const FREE_SHIPPING_THRESHOLD = 50000;
const SHIPPING_COST = 2500;

const CheckoutPage = () => {
  const { user, cart, api, clearCart } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    recipient_name: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    if (!user) {
      toast.error('Debés iniciar sesión para continuar');
      navigate('/login');
    }
    if (cart.items.length === 0) {
      navigate('/productos');
    }
  }, [user, cart, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderResponse = await api.post('/orders', {
        shipping_address: {
          recipient_name: formData.recipient_name,
          street_address: formData.street_address,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postal_code,
          phone: formData.phone
        },
        notes: formData.notes
      });

      // Simulate payment
      await api.post(`/payments/simulate/${orderResponse.data.order_id}`);
      
      toast.success('¡Pedido realizado con éxito!');
      navigate(`/pedido/${orderResponse.data.order_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pedido');
    } finally {
      setLoading(false);
    }
  };

  const shipping = cart.total >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;
  const total = cart.total + shipping;

  return (
    <div className="container mx-auto px-4 py-8" data-testid="checkout-page">
      <h1 className="text-2xl font-bold mb-8">Checkout</h1>
      
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Datos de Envío</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="recipient_name">Nombre completo *</Label>
                    <Input
                      id="recipient_name"
                      required
                      value={formData.recipient_name}
                      onChange={(e) => setFormData({...formData, recipient_name: e.target.value})}
                      data-testid="input-recipient-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      data-testid="input-phone"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="street_address">Dirección *</Label>
                  <Input
                    id="street_address"
                    required
                    value={formData.street_address}
                    onChange={(e) => setFormData({...formData, street_address: e.target.value})}
                    data-testid="input-address"
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">Ciudad *</Label>
                    <Input
                      id="city"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      data-testid="input-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">Provincia *</Label>
                    <Input
                      id="state"
                      required
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      data-testid="input-state"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postal_code">Código Postal *</Label>
                    <Input
                      id="postal_code"
                      required
                      value={formData.postal_code}
                      onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                      data-testid="input-postal-code"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notas (opcional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Instrucciones especiales de entrega..."
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Pago con Mercado Pago
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm mb-4">
                  Al confirmar, serás redirigido a Mercado Pago para completar el pago de forma segura.
                </p>
                <div className="p-4 rounded-lg bg-muted flex items-center gap-4">
                  <CreditCard className="h-8 w-8 text-primary" />
                  <div>
                    <p className="font-medium">Hasta 12 cuotas sin interés</p>
                    <p className="text-sm text-muted-foreground">Tarjetas de crédito y débito</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button 
              type="submit" 
              size="lg" 
              className="w-full btn-accent"
              disabled={loading}
              data-testid="confirm-order-button"
            >
              {loading ? 'Procesando...' : `Confirmar Pedido - $${total.toLocaleString('es-AR')}`}
            </Button>
          </form>
        </div>

        {/* Order Summary */}
        <div>
          <Card className="sticky top-24">
            <CardHeader>
              <CardTitle>Resumen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {cart.items.map((item) => (
                <div key={item.product_id} className="flex gap-3">
                  {item.image && (
                    <img src={item.image} alt="" className="w-16 h-16 rounded object-cover" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    <p className="text-sm text-muted-foreground">Cant: {item.quantity}</p>
                    <p className="text-sm font-semibold">${(item.price * item.quantity).toLocaleString('es-AR')}</p>
                  </div>
                </div>
              ))}
              
              <Separator />
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>${cart.total.toLocaleString('es-AR')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Envío</span>
                  <span>{shipping === 0 ? 'Gratis' : `$${shipping.toLocaleString('es-AR')}`}</span>
                </div>
                {shipping > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Envío gratis en compras mayores a $50.000
                  </p>
                )}
              </div>
              
              <Separator />
              
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span className="text-price">${total.toLocaleString('es-AR')}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const OrderConfirmationPage = () => {
  const { api, user } = useApp();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        console.error('Order fetch failed:', err?.message);
        toast.error('Error al cargar el pedido');
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchOrder();
  }, [api, orderId, user]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-64 w-full max-w-2xl mx-auto" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <h2 className="text-xl font-semibold">Pedido no encontrado</h2>
        <Button asChild className="mt-4">
          <Link to="/mis-pedidos">Ver Mis Pedidos</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="order-confirmation-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <h1 className="text-2xl font-bold">¡Pedido Confirmado!</h1>
          <p className="text-muted-foreground mt-2">Tu pedido #{order.order_id} fue procesado correctamente</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Detalles del Pedido</CardTitle>
              <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                {order.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.product_id} className="flex gap-3">
                  {item.image && (
                    <img src={item.image} alt="" className="w-16 h-16 rounded object-cover" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-muted-foreground">Cant: {item.quantity} x ${item.price.toLocaleString('es-AR')}</p>
                  </div>
                  <p className="font-semibold">${(item.price * item.quantity).toLocaleString('es-AR')}</p>
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${order.subtotal.toLocaleString('es-AR')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Envío</span>
                <span>{order.shipping_cost === 0 ? 'Gratis' : `$${order.shipping_cost.toLocaleString('es-AR')}`}</span>
              </div>
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span>${order.total.toLocaleString('es-AR')}</span>
              </div>
            </div>

            <Separator />

            <div>
              <h4 className="font-semibold mb-2">Dirección de Envío</h4>
              <p className="text-muted-foreground">
                {order.shipping_address?.recipient_name}<br />
                {order.shipping_address?.street_address}<br />
                {order.shipping_address?.city}, {order.shipping_address?.state}<br />
                CP: {order.shipping_address?.postal_code}
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex gap-4">
            <Button asChild className="flex-1">
              <Link to="/mis-pedidos">Ver Mis Pedidos</Link>
            </Button>
            <Button asChild variant="outline" className="flex-1">
              <Link to="/productos">Seguir Comprando</Link>
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

const MyOrdersPage = () => {
  const { api, user } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    const fetchOrders = async () => {
      try {
        const response = await api.get('/orders');
        setOrders(response.data.orders);
      } catch (err) {
        console.error('Orders fetch failed:', err?.message);
        toast.error('Error al cargar pedidos');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [api, user, navigate]);

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    confirmed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    shipped: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    delivered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    cancelled: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
  };

  const statusLabels = {
    pending: 'Pendiente',
    confirmed: 'Confirmado',
    shipped: 'Enviado',
    delivered: 'Entregado',
    cancelled: 'Cancelado'
  };

  return (
    <div className="container mx-auto px-4 py-8" data-testid="my-orders-page">
      <h1 className="text-2xl font-bold mb-8">Mis Pedidos</h1>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={`skeleton-order-${i}`} className="h-32 w-full" />
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20">
          <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold">No tenés pedidos aún</h3>
          <p className="text-muted-foreground">¡Empezá a comprar!</p>
          <Button asChild className="mt-4 btn-accent">
            <Link to="/productos">Ver Productos</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <Card key={order.order_id} className="card-hover">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <p className="font-mono text-sm text-muted-foreground">{order.order_id}</p>
                    <p className="font-semibold text-lg">${order.total.toLocaleString('es-AR')}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString('es-AR')}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge className={statusColors[order.status]}>
                      {statusLabels[order.status]}
                    </Badge>
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/pedido/${order.order_id}`}>Ver Detalles</Link>
                    </Button>
                  </div>
                </div>
                <div className="mt-4 flex gap-2 overflow-x-auto">
                  {order.items.slice(0, 4).map((item) => (
                    item.image && (
                      <img 
                        key={item.product_id} 
                        src={item.image} 
                        alt="" 
                        className="w-12 h-12 rounded object-cover shrink-0"
                      />
                    )
                  ))}
                  {order.items.length > 4 && (
                    <div className="w-12 h-12 rounded bg-muted flex items-center justify-center text-sm">
                      +{order.items.length - 4}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ============== ADMIN PAGES ==============

const AdminDashboard = () => {
  const { api, user } = useApp();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    
    const fetchStats = async () => {
      try {
        const response = await api.get('/admin/dashboard');
        setStats(response.data);
      } catch (err) {
        console.error('Dashboard fetch failed:', err?.message);
        toast.error('Error al cargar estadísticas');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [api, user, navigate]);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={`skeleton-admin-${i}`} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold">Panel de Administración</h1>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/admin/productos">Productos</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/admin/pedidos">Pedidos</Link>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-primary/10">
                <Box className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Productos</p>
                <p className="text-2xl font-bold">{stats?.total_products || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-accent/10">
                <Package className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pedidos</p>
                <p className="text-2xl font-bold">{stats?.total_orders || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-success/10">
                <TrendingUp className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Ingresos</p>
                <p className="text-2xl font-bold">${(stats?.total_revenue || 0).toLocaleString('es-AR')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-warning/10">
                <AlertCircle className="h-6 w-6 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Stock Bajo</p>
                <p className="text-2xl font-bold">{stats?.low_stock_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Pedidos Recientes</CardTitle>
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin/pedidos">Ver Todos</Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats?.recent_orders?.map((order) => (
                <TableRow key={order.order_id}>
                  <TableCell className="font-mono text-sm">{order.order_id}</TableCell>
                  <TableCell>${order.total.toLocaleString('es-AR')}</TableCell>
                  <TableCell>
                    <Badge variant={order.status === 'confirmed' ? 'default' : 'secondary'}>
                      {order.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(order.created_at).toLocaleDateString('es-AR')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

const getStockVariant = (quantity) => {
  if (quantity > 5) return 'default';
  if (quantity > 0) return 'secondary';
  return 'destructive';
};

const AdminProductsPage = () => {
  const { api, user } = useApp();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState({
    sku: '',
    title: '',
    description: '',
    price: '',
    compare_price: '',
    quantity: '',
    category_id: '',
    brand: '',
    images: ''
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const fetchData = useCallback(async () => {
    try {
      const [prodRes, catRes] = await Promise.all([
        api.get('/admin/products?limit=100'),
        api.get('/categories')
      ]);
      setProducts(prodRes.data.products);
      setCategories(catRes.data.categories);
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const handleSubmit = async () => {
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        compare_price: formData.compare_price ? parseFloat(formData.compare_price) : null,
        quantity: parseInt(formData.quantity),
        images: formData.images ? formData.images.split(',').map(s => s.trim()) : []
      };

      if (editProduct) {
        await api.put(`/products/${editProduct.product_id}`, data);
        toast.success('Producto actualizado');
      } else {
        await api.post('/products', data);
        toast.success('Producto creado');
      }

      setShowAddDialog(false);
      setEditProduct(null);
      setFormData({ sku: '', title: '', description: '', price: '', compare_price: '', quantity: '', category_id: '', brand: '', images: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setFormData({
      sku: product.sku,
      title: product.title,
      description: product.description || '',
      price: product.price.toString(),
      compare_price: product.compare_price?.toString() || '',
      quantity: product.quantity.toString(),
      category_id: product.category_id || '',
      brand: product.brand || '',
      images: product.images?.join(', ') || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (productId) => {
    if (!confirm('¿Eliminar este producto?')) return;
    try {
      await api.delete(`/products/${productId}`);
      toast.success('Producto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSyncML = async (productId) => {
    try {
      await api.post(`/admin/ml/sync/${productId}`);
      toast.success('Producto sincronizado con Mercado Libre');
      fetchData();
    } catch (error) {
      toast.error('Error al sincronizar');
    }
  };

  return (
    <div className="container mx-auto px-4 py-8" data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold">Gestión de Productos</h1>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/admin">
              <Home className="h-4 w-4 mr-2" />
              Dashboard
            </Link>
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="btn-accent" onClick={() => { setEditProduct(null); setFormData({ sku: '', title: '', description: '', price: '', compare_price: '', quantity: '', category_id: '', brand: '', images: '' }); }}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Producto
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>SKU *</Label>
                    <Input value={formData.sku} onChange={(e) => setFormData({...formData, sku: e.target.value})} />
                  </div>
                  <div>
                    <Label>Marca</Label>
                    <Input value={formData.brand} onChange={(e) => setFormData({...formData, brand: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Título *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Precio *</Label>
                    <Input type="number" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} />
                  </div>
                  <div>
                    <Label>Precio Comparación</Label>
                    <Input type="number" value={formData.compare_price} onChange={(e) => setFormData({...formData, compare_price: e.target.value})} />
                  </div>
                  <div>
                    <Label>Stock *</Label>
                    <Input type="number" value={formData.quantity} onChange={(e) => setFormData({...formData, quantity: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label>Categoría</Label>
                  <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>URLs de Imágenes (separadas por coma)</Label>
                  <Textarea value={formData.images} onChange={(e) => setFormData({...formData, images: e.target.value})} placeholder="https://..." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancelar</Button>
                <Button className="btn-accent" onClick={handleSubmit}>
                  {editProduct ? 'Guardar Cambios' : 'Crear Producto'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Producto</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead>Precio</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>ML</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => (
                  <TableRow key={product.product_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {product.images?.[0] && (
                          <img src={product.images[0]} alt="" className="w-10 h-10 rounded object-cover" />
                        )}
                        <span className="font-medium truncate max-w-[200px]">{product.title}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                    <TableCell>${product.price.toLocaleString('es-AR')}</TableCell>
                    <TableCell>
                      <Badge variant={getStockVariant(product.quantity)}>
                        {product.quantity}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {product.ml_item_id ? (
                        <Badge variant="outline" className="text-xs">
                          {product.ml_item_id}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(product)}>
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleSyncML(product.product_id)} title="Sincronizar con ML">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(product.product_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AdminOrdersPage = () => {
  const { api, user } = useApp();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchOrders();
  }, [user, navigate, fetchOrders]);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/admin/orders?limit=100');
      setOrders(response.data.orders);
    } catch (error) {
      console.error('Failed to fetch orders', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}`, { status });
      toast.success('Estado actualizado');
      fetchOrders();
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const statusOptions = ['pending', 'confirmed', 'shipped', 'delivered', 'cancelled'];

  return (
    <div className="container mx-auto px-4 py-8" data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold">Gestión de Pedidos</h1>
        <Button asChild variant="outline">
          <Link to="/admin">
            <Home className="h-4 w-4 mr-2" />
            Dashboard
          </Link>
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID Pedido</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Pago</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell className="font-mono text-sm">{order.order_id}</TableCell>
                    <TableCell>{order.shipping_address?.recipient_name || '-'}</TableCell>
                    <TableCell className="font-semibold">${order.total.toLocaleString('es-AR')}</TableCell>
                    <TableCell>
                      <Select value={order.status} onValueChange={(v) => updateOrderStatus(order.order_id, v)}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map(s => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                        {order.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleDateString('es-AR')}</TableCell>
                    <TableCell>
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/pedido/${order.order_id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== MAIN APP ==============

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (auth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallbackPage />;
  }

  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/productos" element={<ProductsPage />} />
      <Route path="/producto/:productId" element={<ProductDetailPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/pedido/:orderId" element={<OrderConfirmationPage />} />
      <Route path="/mis-pedidos" element={<MyOrdersPage />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/admin/productos" element={<AdminProductsPage />} />
      <Route path="/admin/pedidos" element={<AdminOrdersPage />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <AppProvider>
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-1">
            <AppRouter />
          </main>
          <Footer />
          {/* Floating WhatsApp Button */}
          <a 
            href="https://wa.me/5491128597105" 
            target="_blank" 
            rel="noopener noreferrer"
            className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all hover:scale-110"
            data-testid="whatsapp-float"
            title="Contactanos por WhatsApp"
          >
            <svg className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </a>
        </div>
        <Toaster position="top-right" richColors />
      </AppProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
